/**
 * iter1238 P1c · StreamingParsedFeed
 * ===================================
 * Live-hydrating list of transactions that have been parsed from the
 * user's uploaded statement but not yet fully processed by the
 * intelligence pipeline. Renders above the classified Bank Feed.
 *
 * Each row shows a subtle "parsing…" badge until it's committed to
 * bank_feed_transactions - at which point it disappears from this
 * component and appears in the normal feed below (rendered by the
 * parent page).
 */
import React from 'react';
import { Loader2, Sparkles, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useStreamingParsedPreview } from '../../hooks/useStreamingParsedPreview';

const fmtAmount = (n) => Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const StreamingParsedFeed = ({ jobId, bankAccountId, onComplete }) => {
  const {
    rows, counts, jobStatus, progressPercent, statusMessage,
    streamingSupported,
  } = useStreamingParsedPreview(jobId, { enabled: !!jobId, onComplete });

  if (!jobId || !streamingSupported) return null;

  // Hide once every row is committed AND the job is complete.
  const hasCommittedAll = counts.pending === 0 && counts.processing === 0
    && counts.committed > 0 && jobStatus === 'completed';
  if (hasCommittedAll && rows.length === 0) return null;

  // iter1238 Phase 8.9 — cancelled / failed jobs must not keep showing
  // the "Parsing your statement live" progress card. If nothing was
  // committed, hide entirely. If some rows were committed before the
  // cancel/fail, render a compact summary the user can dismiss.
  const isTerminated = jobStatus === 'cancelled' || jobStatus === 'failed';
  if (isTerminated && counts.committed === 0) return null;
  if (isTerminated) {
    const label = jobStatus === 'cancelled' ? 'Upload cancelled' : 'Upload failed';
    return (
      <div
        data-testid="streaming-parsed-feed-terminated"
        className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${jobStatus === 'cancelled' ? 'bg-slate-400' : 'bg-rose-500'}`} />
            <h3 className="text-sm font-semibold text-slate-800">{label}</h3>
          </div>
          <span className="text-xs text-slate-500" data-testid="streaming-terminated-status">
            {counts.committed} row{counts.committed === 1 ? '' : 's'} imported before stop
          </span>
        </div>
      </div>
    );
  }

  const inFlightRows = rows.filter(
    (r) => (r.bank_account_id === bankAccountId || !bankAccountId)
    && r.downstream_status !== 'committed',
  );

  return (
    <div
      data-testid="streaming-parsed-feed"
      className="mb-4 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-4 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-indigo-900">
            Parsing your statement live
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Loader2 className="w-3 h-3 animate-spin text-indigo-500" />
          <span data-testid="streaming-status-message">
            {statusMessage || 'Working…'}
          </span>
        </div>
      </div>

      <div
        className="h-1.5 w-full rounded-full bg-indigo-100 overflow-hidden mb-3"
        data-testid="streaming-progress-bar-wrap"
      >
        <div
          className="h-full bg-indigo-500 transition-all duration-500 ease-out"
          style={{ width: `${Math.max(4, Math.min(100, progressPercent))}%` }}
          data-testid="streaming-progress-bar"
        />
      </div>

      <div className="grid grid-cols-4 gap-2 text-center mb-3 text-xs">
        <div className="rounded-md bg-white/70 p-2">
          <div className="text-lg font-bold text-slate-800" data-testid="streaming-counter-pending">
            {counts.pending}
          </div>
          <div className="text-slate-500">Awaiting classify</div>
        </div>
        <div className="rounded-md bg-white/70 p-2">
          <div className="text-lg font-bold text-indigo-700" data-testid="streaming-counter-processing">
            {counts.processing}
          </div>
          <div className="text-slate-500">Classifying</div>
        </div>
        <div className="rounded-md bg-white/70 p-2">
          <div className="text-lg font-bold text-emerald-700" data-testid="streaming-counter-committed">
            {counts.committed}
          </div>
          <div className="text-slate-500">Live in feed</div>
        </div>
        <div className="rounded-md bg-white/70 p-2">
          <div className="text-lg font-bold text-slate-700" data-testid="streaming-counter-total">
            {rows.length}
          </div>
          <div className="text-slate-500">Parsed so far</div>
        </div>
      </div>

      {inFlightRows.length > 0 && (
        <ul
          className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200 bg-white"
          data-testid="streaming-rows-list"
        >
          {inFlightRows.slice(-40).reverse().map((r) => (
            <li
              key={`${r.job_id}-${r.row_index}`}
              className="flex items-center justify-between gap-3 py-1.5 px-3 text-xs hover:bg-slate-50"
              data-testid={`streaming-row-${r.row_index}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {r.direction === 'inflow' ? (
                  <ArrowUpCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <ArrowDownCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                )}
                <span className="text-slate-500 tabular-nums">{r.date}</span>
                <span className="truncate text-slate-800">{r.description}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`tabular-nums font-medium ${
                    r.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'
                  }`}
                >
                  {r.direction === 'inflow' ? '+' : '−'}
                  {fmtAmount(r.amount)}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  parsed
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StreamingParsedFeed;
